// The per-box slot ceiling (#7004): how many tasks ONE box may hold at once.
// Split out of `../runner.ts`, which is the barrel over this directory.
//
// Three bounds decide what a box may be handed — the stored `runners.max_slots`
// column, the box's own beat advert (`BoxBeatSlots`) and a hardware-derived
// specs ceiling — and every reader takes `boxSlotCap`'s fold over them, never
// one input alone. The stored bound itself — `RUNNER_MAX_SLOTS_MIN/MAX/DEFAULT`
// and `RunnerMaxSlotsSchema` — lives in `./max-slots` (#6740); this module holds
// the beat advert and the fold, and imports the bounds without re-exporting
// them, so the barrel exports each name exactly once.
//
// #7506 adds the SPECS half of that fold and the wire it rides on: the
// hardware ceiling is derived from a MEASUREMENT of what one run actually costs
// (`SLOT_TREE_PEAK_MIB`, `boxSlotCeiling`) rather than from a hand-picked
// constant, and the box's slot state crosses the wire as `BoxSlotsSchema` —
// with the refusal vocabulary `SLOT_REFUSALS` that says WHY a box is not taking
// work.

import { z } from 'zod';
import { RUNNER_MAX_SLOTS_MAX, RUNNER_MAX_SLOTS_MIN, RunnerMaxSlotsSchema } from './max-slots';
import { sizeUnitMemory } from './memory-posture';
import { BOX_ENROLL_RAM_FLOOR_BYTES, type BoxSpecs } from './specs';

/**
 * Why a box will not take a task RIGHT NOW — the COMPLETE vocabulary a box may
 * advertise, and the only one: a refusal a reader does not know is a refusal it
 * cannot act on.
 *
 * Every member names a fact the BOX already measures:
 *   - `full` — the box holds as many tasks as its own ceiling allows.
 *   - `ci_running` — a CI job occupies the box (the CI plane counts separately
 *     from the task plane: a `ci_jobs` row `assigned` or `running`).
 *   - `draining` — the box is on its way out (the same fact the presence beat's
 *     `draining` field carries).
 *   - `settling` — the box admitted a job and no full CPU window that started
 *     at or after that admit has completed yet, so it cannot know what the new
 *     job costs (#6738).
 *   - `cpu_busy` — true CPU busy (`/proc/stat`, iowait idle, steal busy) is at
 *     or over 85% (`SLOT_ADMISSION_CPU_BUSY_PCT`, `apps/runner` — owner-ruled;
 *     NOT the machine verdict's load-ratio `MACHINE_CPU_BUSY_RATIO`).
 *   - `memory_low` — MemAvailable is under one job's p95 whole-tree peak
 *     ({@link SLOT_TREE_PEAK_MIB}).
 *   - `disk_low` — free workspace disk is under the box's own floor plus one
 *     task's default disk need (`DEFAULT_DISK_PER_TASK_GB`).
 *   - `unobservable` — the box could not read the gauge it would need. NO
 *     reading, never a zero.
 *
 * The last five are the box's ADMISSION verdict (`apps/runner/src/slot-admission.ts`)
 * and apply only to a box already holding a job; an empty box always accepts.
 *
 * `unobservable` is deliberately a MEMBER and not an absence: "I could not
 * measure" and "I measured nothing wrong" are different answers, and a reader
 * that collapses them reads silence as health.
 */
export const SLOT_REFUSALS = [
  'full',
  'ci_running',
  'draining',
  'settling',
  'cpu_busy',
  'memory_low',
  'disk_low',
  'unobservable',
] as const;

/**
 * The refusal vocabulary as a schema — the wire form, so a refusal that is not
 * one of {@link SLOT_REFUSALS} is REFUSED rather than carried as a word no
 * reader knows.
 */
export const SlotRefusalSchema = z.enum(SLOT_REFUSALS);

/**
 * What a box says about its SLOTS: how many tasks it may hold at once
 * (`ceiling`), how many runs it has live right now (`live`), whether it would
 * pull another task (`accepting`), and — when it would not — why (`refusal`).
 *
 * `ceiling` is the box's OWN view of its capacity: the smallest of the stored
 * cap, its hardware ceiling ({@link boxSlotCeiling}) and what it can actually
 * run.
 *
 * `live` is this box's live runs and nothing else: a PAUSED job is not one, so
 * `live` is not the box's occupancy. Its only mothership reader is the
 * assigner's `beat_behind` skip (`slotSkipOf`, `apps/worker/src/dispatch/presence-skips.ts`),
 * which compares it against the ledger's held count to tell a beat that
 * predates the newest claim.
 *
 * `accepting` is a separate answer: whether the box would pull another task.
 * It counts live runs plus work the box pulled and has not started yet, and it
 * reads the box's admission verdict.
 *
 * Neither field is the occupancy a placement compares against the cap. The
 * slot a paused job holds is kept by the MOTHERSHIP LEDGER, not by this beat:
 * `pinnedReservationSql` (`packages/db/src/queries/tasks/fence.ts`) is the one
 * predicate every placement reader folds in, and `countBoxSlots` reads it as
 * `reserved` beside `held`. The cap folds `ceiling` in through
 * {@link boxSlotCap}: the minimum of `max_slots`, `ceiling` and the specs
 * ceiling.
 *
 * Every count is a WHOLE number at or above zero, never a float and never a
 * negative: a negative count is a broken producer, and a fractional one would
 * make any comparison against it unreproducible.
 */
export const BoxSlotsSchema = z.object({
  /** How many tasks this box may hold at once. */
  ceiling: z.number().int().nonnegative(),
  /**
   * How many runs it has LIVE right now. A paused job is not one, so this is not the
   * box's occupancy: the ledger keeps a paused job's slot (`pinnedReservationSql`).
   */
  live: z.number().int().nonnegative(),
  /**
   * Whether it would pull another task: live runs plus work pulled and not yet started,
   * read against its admission verdict. Not an occupancy either.
   */
  accepting: z.boolean(),
  /** Why not, when `accepting` is false. Absent means "no reason given". */
  refusal: SlotRefusalSchema.optional(),
  /**
   * The per-run control verbs this build understands beyond the cancel (#6749). Absent
   * means none: the API refuses a pause to a box whose beat does not list it (#6751), which
   * is how an older box is never sent one. A literal per verb, never an enum this box writes
   * into a closed set; an older mothership strips the key.
   */
  verbs: z.array(z.literal('pause')).optional(),
});
export type BoxSlots = z.infer<typeof BoxSlotsSchema>;

/**
 * The slot ceiling a box's own beat advertises for itself. Absent (null at the
 * reader) while the box predates the advert or has not beaten yet: the fold
 * then trusts the box with ONE slot, the pre-#7004 capacity-1 behaviour.
 *
 * The shape is {@link BoxSlots}'s ceiling and nothing else: the advert carries
 * ONE number, and the live-run count, the accepting verdict and the refusal ride
 * beside it in the wire shape, not in the advert the capacity fold reads. The
 * occupancy the fold's cap is compared against is the ledger's (`countBoxSlots`:
 * held plus the reservations `pinnedReservationSql` keeps for paused jobs), never
 * the beat's `live`.
 */
export type BoxBeatSlots = Pick<BoxSlots, 'ceiling'>;

/**
 * The per-run whole-tree memory high-water mark (MiB) — what ONE coding run
 * costs, MEASURED on prod — and the input {@link boxSlotCeiling} divides the
 * box's task budget by.
 *
 * It is a MEASUREMENT, not a number anyone may pick: the ceiling it produces
 * is how many coding tasks one box may hold at once, and a guess here is either
 * an OOM (too many slots) or a starved box (too few). The measurement is the
 * `run_tree_peak_mb` a box stamps on its `run_finished` event — the run
 * cgroup's `memory.peak`, the WHOLE tree's high-water mark, the gate's `tsc`
 * and every other child included (`apps/runner/src/run-scope.ts`
 * `withRunTreePeak`) — persisted in `task_run_events`.
 *
 * **THE VALUE IS THE p95 OF THE RECORDED POPULATION** (`percentile_disc`, so it is
 * a reading some run actually produced, never an interpolation between two):
 *
 *   - value: **5,621 MiB** — the 95th-percentile `run_tree_peak_mb`.
 *   - sample count n: **6,455** runs carried the field.
 *   - denominator: **6,455 of 8,115** `run_finished` rows in the window (the
 *     rest have no per-run cgroup, so no reading — review runs among them).
 *   - window: **2026-10-15T00:00Z to 2026-10-29T00:00Z** (14 days, literal).
 *   - date: measured **2026-10-29** (#7506), by a seat holding the operator key.
 *   - beside it, same window: `percentile_cont` 5,609.8 MiB, p50 719 MiB, max
 *     12,517 MiB. The issue's rolling form (`ts > now() - interval '14 days'`, read
 *     2026-10-29 11:57Z) gave 6,621 runs and p95 5,569 MiB.
 *
 * It REPLACES the 4,353 MiB this constant first carried (#7509): the max of three
 * runs on one build, landed while prod refused the read, with this re-measurement as
 * its stated trip condition. The p95 is LARGER than that max, so the ceiling it
 * yields is smaller (a 16 GiB box: 2 slots at 4,353, 1 at 5,621) — the thin sample
 * had promised more concurrency than the population supports.
 *
 * A p95 and not the max on purpose: the max (12,517 MiB) is one outlier run, and
 * dividing by it would starve every box to keep a 1-in-6,455 run inside the shared
 * `MemoryHigh`. A run past the p95 is what the unit's `MemoryHigh` reclaim and the
 * per-run cgroup cap are for; the CPU/memory admission (#6738) is the live guard.
 *
 * **RE-MEASURE** with this one statement (`docs/standards/measurements.md` — a
 * measurement in the tree carries its query, its window and its date), moving the
 * literal window forward and restating every figure above:
 *
 *     SELECT
 *       count(*) AS run_finished_rows,
 *       count(*) FILTER (WHERE payload->>'run_tree_peak_mb' ~ '^[0-9]+$')
 *         AS runs_carrying_the_field,
 *       percentile_disc(0.95) WITHIN GROUP (
 *         ORDER BY (payload->>'run_tree_peak_mb')::numeric
 *       ) AS p95_mib,
 *       max((payload->>'run_tree_peak_mb')::numeric) AS max_mib
 *     FROM task_run_events
 *     WHERE kind = 'status'
 *       AND payload->>'event' = 'run_finished'
 *       AND ts >= '2026-10-15T00:00:00Z'::timestamptz
 *       AND ts < '2026-10-29T00:00:00Z'::timestamptz
 *
 * Read it with `bun run infra psql "<the SELECT above>"` — one statement, no
 * trailing `;` (the verb refuses a second statement). The window is
 * LITERAL on purpose: `now() - interval` makes a recorded number
 * unreproducible by construction (`docs/standards/measurements.md` § `now() -
 * interval` is not a window). The denominator is `runs_carrying_the_field` —
 * a p95 over the runs that carried the field, never over every `run_finished`
 * row, because a run with no per-run cgroup has no reading. Under 200 carrying
 * runs, widen the window rather than trust the sample.
 */
export const SLOT_TREE_PEAK_MIB: number = 5_621;

/**
 * The ceiling a box's HARDWARE allows: how many tasks it may hold at once,
 * derived from the memory it has and the measured cost of one run.
 *
 *   1. No specs at all, or specs under the enrollment RAM floor
 *      ({@link BOX_ENROLL_RAM_FLOOR_BYTES}) → ONE slot. A box that never
 *      advertised its size, or advertised one below the floor the join script
 *      refuses, is trusted with the pre-#7004 capacity-1 behaviour. The floor
 *      check comes FIRST because {@link sizeUnitMemory} THROWS under it: a
 *      caller holding an under-floor box must get a ceiling, not an exception.
 *   2. Otherwise: the box's task budget — `sizeUnitMemory(specs.memBytes)
 *      .taskCapMib`, the unit's `memory.high` minus the daemon's headroom —
 *      divided by the measured cost of one run ({@link SLOT_TREE_PEAK_MIB}),
 *      floored to whole slots and clamped to [1, {@link RUNNER_MAX_SLOTS_MAX}].
 *
 * The clamp at {@link RUNNER_MAX_SLOTS_MAX} is the stored cap's own ceiling
 * (`runners.max_slots`, bounded by the same constant), so the hardware can
 * never promise more concurrency than the fleet is allowed to record.
 *
 * `slotTreePeakMib` is a PARAMETER so the formula is testable against another
 * measurement; it defaults to {@link SLOT_TREE_PEAK_MIB}, whose docblock says
 * which statistic that value is. A peak that is not a positive finite number
 * THROWS, naming the value: a zero peak divides to Infinity and the clamp
 * would silently hand the box the fleet maximum, and a negative one would hand
 * it a ceiling nobody can reproduce.
 *
 * The guard sits AFTER the two capacity-1 branches and BEFORE the division: a
 * box that never advertised its hardware (or advertised an under-floor one)
 * gets its one slot whatever the peak says, and a broken peak is refused
 * before it can divide.
 */
export function boxSlotCeiling(
  specs: BoxSpecs | null | undefined,
  slotTreePeakMib: number = SLOT_TREE_PEAK_MIB,
): number {
  if (specs === null || specs === undefined) return 1;
  if (specs.memBytes < BOX_ENROLL_RAM_FLOOR_BYTES) return 1;
  if (!Number.isFinite(slotTreePeakMib) || slotTreePeakMib <= 0) {
    throw new RangeError(
      `boxSlotCeiling: the run-tree peak must be a positive finite number of MiB, got ${String(slotTreePeakMib)} — measure it with the query in SLOT_TREE_PEAK_MIB`,
    );
  }
  const { taskCapMib } = sizeUnitMemory(specs.memBytes);
  return Math.min(RUNNER_MAX_SLOTS_MAX, Math.max(1, Math.floor(taskCapMib / slotTreePeakMib)));
}

/**
 * The per-box capacity a dispatcher may use: the SMALLEST of the stored
 * ceiling (`maxSlots`), the beat advert (`beatSlots.ceiling`) and the
 * hardware-derived `specsCeiling`, floored at 1. A box with no beat advert yet
 * (`beatSlots === null`) is capacity 1.
 *
 * Throws a RangeError naming the value and the column's `runners_max_slots_ck`
 * when `maxSlots` is outside {@link RunnerMaxSlotsSchema} — the caller's stored
 * column is broken, and a bare ZodError would not say which value broke it.
 */
export function boxSlotCap({
  maxSlots,
  beatSlots,
  specsCeiling,
}: {
  maxSlots: number;
  beatSlots: BoxBeatSlots | null;
  specsCeiling: number | null | undefined;
}): number {
  const parsed = RunnerMaxSlotsSchema.safeParse(maxSlots);
  if (!parsed.success) {
    throw new RangeError(
      `maxSlots must be an integer between ${RUNNER_MAX_SLOTS_MIN} and ${RUNNER_MAX_SLOTS_MAX} (runners_max_slots_ck), got ${String(maxSlots)}`,
    );
  }
  if (beatSlots === null) return 1;
  return Math.max(1, Math.min(maxSlots, beatSlots.ceiling, specsCeiling ?? Infinity));
}
